import React from 'react';
import { IUserAddress } from 'app/address'; // Import the type
import { Container, TextField, Button, Typography, Checkbox, FormControlLabel, Grid, CircularProgress } from '@mui/material';
import { useForm, Controller, Control } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { addAAddress, updateAAddress } from '../app/services/address/addressAPI';
import { findUserId } from '../app/services/UtilServices';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';

const addressSchema = yup.object().shape({
    name: yup.string().required("Name is required"),
    phone: yup.string().required("Phone is required"),
    pincode: yup.string().required("Pincode is required"),
    locality: yup.string().required("Locality is required"),
    addressLine1: yup.string().required("Address Line 1 is required"),
    addressLine2: yup.string().optional(),
    city: yup.string().required("City is required"),
    state: yup.string().required("State is required"),
    landmark: yup.string().optional(),
    alternatePhone: yup.string().optional(),
    location: yup.object().shape({
        type: yup.string(),
        coordinates: yup.array().of(yup.number())
    }).optional()
});

// Define the missing props interface
interface IAddressFormProps {
    data: IUserAddress | null;
    onAddressAddOrUpdate: (isNew: boolean, address: IUserAddress) => void;
}

// First, update the interface for form values
interface IFormInputs {
    name: string;
    phone: string;
    pincode: string;
    locality: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    landmark?: string;
    alternatePhone?: string;
    location?: {
        type: string;
        coordinates: number[];
    };
}

const AddressForm: React.FC<IAddressFormProps> = ({ data, onAddressAddOrUpdate }) => {
    const [isLoading, setLoading] = React.useState(false);
    const [addressType, setAddressType] = React.useState<'home' | 'work'>(data?.addressType || 'home');
    const [isDefaultAddress, setDefaultAddress] = React.useState(data?.isDefaultAddress || false);

    // Prepare initial values for the form fields managed by Controller
    const initialFormValues = React.useMemo(() => {
        // Base defaults for controlled fields
        const defaults = {
            name: '',
            phone: '',
            pincode: '',
            locality: '',
            addressLine1: '',
            addressLine2: '',
            city: 'Murshidabad',
            state: 'West Bengal',
            landmark: '',
            alternatePhone: '',
            location: { type: 'Point', coordinates: [0, 0] } as IUserAddress['location'] // Ensure type
        };
        // Merge with incoming data, selecting only relevant fields
        const relevantData = data ? {
            name: data.name,
            phone: data.phone,
            pincode: data.pincode,
            locality: data.locality,
            addressLine1: data.addressLine1,
            addressLine2: data.addressLine2 || '',
            city: data.city,
            state: data.state,
            landmark: data.landmark || '',
            alternatePhone: data.alternatePhone || '',
            location: data.location || defaults.location
        } : {};

        // Exclude fields handled by separate state (addressType, isDefaultAddress)
        // Exclude userId as it's added during submission
        return { ...defaults, ...relevantData };

    }, [data]); // Dependency is only the incoming data prop

    // Update the form initialization with proper typing
    const { control, handleSubmit, formState: { errors } } = useForm<IFormInputs>({
        //@ts-ignore
        resolver: yupResolver<IFormInputs>(addressSchema),
        // Provide the simplified, pre-calculated default values
        defaultValues: initialFormValues
    });

    // Update the onSubmit handler to use IFormInputs type
    const onSubmit = async (formData: IFormInputs) => {
        setLoading(true);
        try {
            // Create the full address data by combining form data with other state
            const addressData: any = {
                ...formData,
                isDefaultAddress,
                userId: findUserId(),
                addressType,
                location: {
                    type: 'Point',
                    coordinates: [3.280292, 3.280292]
                }
            };

            const result = await (data?._id
                ? updateAAddress(data._id, addressData)
                : addAAddress(addressData));

            if (result?.success) {
                toast.success(`Address ${data ? 'updated' : 'added'} successfully`);
                onAddressAddOrUpdate(data == null, result.data);
            } else {
                toast.error(result?.message || 'Error saving address');
            }
        } catch (error) {
            toast.error('Failed to save address');
        }
        setLoading(false);
    };

    return (
        <Container maxWidth="md">
            {isLoading && <CircularProgress />}
            {/* //@ts-ignore */}
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Controller<IFormInputs>
                            name="name"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Full Name"
                                    fullWidth
                                    error={!!errors.name}
                                    helperText={errors.name?.message}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller<IFormInputs>
                            name="phone"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Mobile Number"
                                    fullWidth
                                    error={!!errors.phone}
                                    helperText={errors.phone?.message}
                                />
                            )}
                        />
                    </Grid>


                    <Grid item xs={12}>
                        <Controller<IFormInputs>
                            name="addressLine1"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Address Line 1"
                                    fullWidth
                                    error={!!errors.addressLine1}
                                    helperText={errors.addressLine1?.message}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Controller<IFormInputs>
                            name="addressLine2"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Address Line 2"
                                    fullWidth
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller<IFormInputs>
                            name="pincode"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Pin Code"
                                    fullWidth
                                    error={!!errors.pincode}
                                    helperText={errors.pincode?.message}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller<IFormInputs>
                            name="locality"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Locality"
                                    fullWidth
                                    error={!!errors.locality}
                                    helperText={errors.locality?.message}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller<IFormInputs>
                            name="state"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="state"
                                    fullWidth
                                    disabled
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller<IFormInputs>
                            name="landmark"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="landmark"
                                    fullWidth
                                    disabled
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller<IFormInputs>
                            name="city"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="City"
                                    fullWidth
                                    disabled
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography color="textSecondary">Type of address</Typography>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item>
                                <Button
                                    variant={addressType === 'home' ? 'contained' : 'outlined'}
                                    onClick={() => setAddressType('home')}
                                    startIcon={<HomeIcon />}
                                >
                                    Home
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant={addressType === 'work' ? 'contained' : 'outlined'}
                                    onClick={() => setAddressType('work')}
                                    startIcon={<BusinessIcon />}
                                >
                                    Work
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isDefaultAddress}
                                    onChange={(e) => setDefaultAddress(e.target.checked)}
                                />
                            }
                            label="Use as default address"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={isLoading}
                            startIcon={isLoading && <CircularProgress size={20} />}
                        >
                            Save Address
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Container>
    );
};

export default AddressForm;
